import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import { logger } from '../utils/logger'
import { isValidCpf, normalizeCpf } from '../utils/cpf'

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  userType: 'model' | 'client' | null
}

interface AuthContextType extends AuthState {
  signUp: (email: string, password: string, userData: any, photos?: File[]) => Promise<any>
  signIn: (email: string, password: string) => Promise<any>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<any>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    userType: null
  })

  useEffect(() => {
    let mounted = true
    let authTimeout: NodeJS.Timeout

    const initializeAuth = async () => {
      if (!isSupabaseConfigured) {
        if (mounted) {
          setState(prev => ({
            ...prev,
            loading: false
          }))
        }
        logger.warn('Demo mode: Authentication disabled')
        return
      }

      try {
        // Timeout de 10 segundos para getSession
        authTimeout = setTimeout(() => {
          if (mounted) {
            console.warn('Auth getSession timeout - setting loading to false')
            setState(prev => ({
              ...prev,
              loading: false
            }))
          }
        }, 10000)

        // Obter sessão inicial usando getSession() que é mais confiável
        const { data: { session }, error } = await supabase.auth.getSession()
        
        clearTimeout(authTimeout)

        if (error) {
          console.error('Error getting session:', error)
          if (mounted) {
            setState(prev => ({
              ...prev,
              loading: false
            }))
          }
          return
        }

        console.log('Initial session:', session ? 'Found' : 'Not found')

        if (mounted) {
          setState(prev => ({
            ...prev,
            session,
            user: session?.user ?? null,
            userType: session?.user?.user_metadata?.user_type ?? null,
            loading: false
          }))
        }

        // Atualizar status online se logado
        if (session?.user) {
          await updateOnlineStatus(session.user.id, true)
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
        clearTimeout(authTimeout)
        if (mounted) {
          setState(prev => ({
            ...prev,
            loading: false
          }))
        }
      }
    }

    initializeAuth()

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session ? 'Session exists' : 'No session')
        
        if (!mounted) return

        setState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
          userType: session?.user?.user_metadata?.user_type ?? null,
          loading: false
        }))

        // Atualizar status online quando logar/deslogar
        if (event === 'SIGNED_IN' && session?.user) {
          await updateOnlineStatus(session.user.id, true)
        } else if (event === 'SIGNED_OUT') {
          // Status offline será atualizado pelo heartbeat
        }
      }
    )

    return () => {
      mounted = false
      clearTimeout(authTimeout)
      subscription.unsubscribe()
    }
  }, [])

  const updateOnlineStatus = async (userId: string, _isOnline: boolean) => {
    if (!isSupabaseConfigured) return

    try {
      const userType = state.userType
      
      if (userType === 'model') {
        await supabase
          .from('profiles')
          .update({ 
            last_seen: new Date().toISOString()
          })
          .eq('id', userId)
      }
    } catch (error) {
      logger.error('Error updating online status', error)
    }
  }

  const signUp = async (email: string, password: string, userData: any, photos: File[] = []) => {
    try {
      console.log('signUp called with:', { email, userType: userData.user_type })
      setState(prev => ({ ...prev, loading: true }))
      
      if (!isSupabaseConfigured) {
        console.log('Demo mode - simulating signup')
        // Modo demo
        await new Promise(resolve => setTimeout(resolve, 1000))
        const demoUser = {
          id: 'demo-user-' + Date.now(),
          email,
          user_metadata: userData
        }
        setState(prev => ({
          ...prev,
          user: demoUser as unknown as User,
          userType: userData.user_type,
          loading: false
        }))
        console.log('Demo signup successful:', demoUser)
        return { data: { user: demoUser }, error: null }
      }

      const cpfDigits = userData?.cpf ? normalizeCpf(userData.cpf) : ''
      if (cpfDigits) {
        if (!isValidCpf(cpfDigits)) {
          throw new Error('CPF inválido')
        }
        userData.cpf = cpfDigits

        // Melhor esforço: verificar CPF duplicado antes do cadastro (quando permitido por RLS/colunas existentes)
        const checkCpf = async (table: 'profiles' | 'clients') => {
          const { data: existing, error: existingError } = await supabase
            .from(table)
            .select('id')
            .eq('cpf', cpfDigits)
            .limit(1)

          if (existingError) {
            logger.warn('CPF precheck skipped', { table, code: (existingError as any).code, message: existingError.message })
            return false
          }

          return Array.isArray(existing) && existing.length > 0
        }

        const existsInProfiles = await checkCpf('profiles')
        const existsInClients = await checkCpf('clients')
        if (existsInProfiles || existsInClients) {
          throw new Error('CPF já cadastrado')
        }
      }

      console.log('Calling Supabase signUp...')
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      })

      if (error) {
        console.error('Supabase signUp error:', error)
        const msg = String(error.message || '')
        if (msg.toLowerCase().includes('already registered')) {
          throw new Error('Email já cadastrado')
        }
        throw error
      }

      console.log('Supabase signUp successful:', data)

      // Criar perfil específico baseado no tipo de usuário
      if (data.user && userData.user_type) {
        try {
          console.log('Creating profile for user type:', userData.user_type)
          if (userData.user_type === 'model') {
            let photoUrls: string[] = []

            if (photos.length > 0) {
              const { imageStorageService } = await import('../services/imageStorageService')
              for (const photo of photos.slice(0, 6)) {
                const uploadResult = await imageStorageService.uploadModelPhoto(data.user.id, photo)
                if (uploadResult.success && uploadResult.url) {
                  photoUrls.push(uploadResult.url)
                } else {
                  console.warn('Photo upload failed during signup:', uploadResult.error)
                }
              }
            }

            // Mapear para colunas existentes na tabela profiles
            const profileData = {
              id: data.user.id,
              user_id: data.user.id,
              email: email,
              full_name: userData.full_name || userData.name || (email ? email.split('@')[0] : 'Modelo'),
              // Alguns lugares do app ainda usam `name` (fallback para compatibilidade)
              name: userData.name || userData.full_name || (email ? email.split('@')[0] : 'Modelo'),
              bio: userData.description || null,
              description: userData.description || null,
              photos: photoUrls,
              profile_image_url: photoUrls[0] || null,
              is_online: false,
              is_model: true,
              approval_status: 'pending',
              verified: false,
              // Campos opcionais do perfil
              age: userData.age || null,
              state: userData.state || null,
              city: userData.city || null,
              neighborhood: userData.neighborhood || null,
              cep: userData.cep || null,
              hourly_rate: userData.hourlyRate || userData.hourly_rate || null,
              services: userData.services || [],
              payment_methods: userData.paymentMethods || userData.payment_methods || [],
              availability: userData.availability || {}
            }
            console.log('Inserting model profile:', profileData)
            
            // Usar upsert para garantir que campos novos sejam gravados
            const profileResult = await supabase
              .from('profiles')
              .upsert(profileData, { onConflict: 'id' })
              .select()

            if (profileResult.error) {
              throw profileResult.error
            }
              
            console.log('Profile insert result:', profileResult)

            // Salvar telefone (opcional). Em bancos antigos a coluna pode não existir, então não falhar o cadastro.
            if (userData.phone) {
              const phoneResult = await supabase
                .from('profiles')
                .update({ phone: userData.phone })
                .eq('id', data.user.id)

              if (phoneResult.error) {
                console.warn('Could not persist phone field on profile (optional)', phoneResult.error)
              }
            }

            // Salvar CPF (opcional). Em bancos antigos a coluna pode não existir, então não falhar o cadastro.
            if (userData.cpf) {
              const cpfResult = await supabase
                .from('profiles')
                .update({ cpf: userData.cpf })
                .eq('id', data.user.id)

              if (cpfResult.error) {
                console.warn('Could not persist cpf field on profile (optional)', cpfResult.error)
              }
            }

            // Reforço: se veio `userData.paymentMethods`/`services`/`description` e não retornou na linha, fazer update direto
            if (!profileResult.data || profileResult.data.length === 0) {
              console.warn('Upsert did not return profile; retrying update for model fields')
              const updateResult = await supabase
                .from('profiles')
                .update({
                  user_id: data.user.id,
                  full_name: userData.full_name || userData.name || (email ? email.split('@')[0] : 'Modelo'),
                  name: userData.name || userData.full_name || (email ? email.split('@')[0] : 'Modelo'),
                  description: userData.description || null,
                  services: userData.services || [],
                  payment_methods: userData.paymentMethods || userData.payment_methods || [],
                  availability: userData.availability || {},
                  is_model: true,
                  approval_status: 'pending',
                  verified: false,
                  age: userData.age || null,
                  state: userData.state || null,
                  city: userData.city || null,
                  neighborhood: userData.neighborhood || null,
                  cep: userData.cep || null,
                  hourly_rate: userData.hourlyRate || userData.hourly_rate || null,
                  profile_image_url: photoUrls[0] || null
                })
                .eq('id', data.user.id)

              if (updateResult.error) {
                throw updateResult.error
              }
            }
          } else if (userData.user_type === 'client') {
            // Para clientes, criar registro na tabela clients E carteira de tokens
            console.log('Creating client record and wallet')
            
            // 1. Criar registro na tabela clients
            const clientResult = await supabase
              .from('clients')
              .insert({
                user_id: data.user.id,
                full_name: userData.name,
                email: email
              })
            console.log('Client insert result:', clientResult)

            // Salvar CPF/telefone (opcional). Em bancos antigos a coluna pode não existir, então não falhar o cadastro.
            if (userData.phone || userData.cpf) {
              const extraResult = await supabase
                .from('clients')
                .update({
                  phone: userData.phone || null,
                  cpf: userData.cpf || null
                })
                .eq('user_id', data.user.id)

              if (extraResult.error) {
                console.warn('Could not persist phone/cpf on clients (optional)', extraResult.error)
              }
            }
            
            // 2. Criar carteira de tokens
            const walletResult = await supabase
              .from('user_wallets')
              .insert({
                user_id: data.user.id,
                token_balance: 100 // Dar 100 tokens iniciais para novos clientes
              })
            console.log('Wallet insert result:', walletResult)
          }
        } catch (profileError) {
          // Log do erro mas não falhar o cadastro
          console.warn('Profile creation error (non-critical):', profileError)
          logger.warn('Profile creation error (non-critical)', profileError)
        }
      }

      // IMPORTANTE: Fazer logout após registro para não logar automaticamente
      // Isso é necessário para quando ativarmos confirmação por email
      console.log('Logging out user after registration to prevent auto-login...')
      await supabase.auth.signOut()
      console.log('User logged out after registration')

      logger.info('User signed up successfully', { userId: data.user?.id, userType: userData.user_type })
      console.log('signUp completed successfully')
      return { data, error: null }
    } catch (error: any) {
      console.error('signUp error:', error)
      logger.error('Sign up error', error)
      return { data: null, error }
    } finally {
      setState(prev => ({ ...prev, loading: false }))
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, loading: true }))
      
      if (!isSupabaseConfigured) {
        // Modo demo
        await new Promise(resolve => setTimeout(resolve, 1000))
        const demoUser = {
          id: 'demo-user-' + Date.now(),
          email,
          user_metadata: { user_type: 'client', name: 'Demo User' }
        }
        setState(prev => ({
          ...prev,
          user: demoUser as unknown as User,
          userType: 'client',
          loading: false
        }))
        return { data: { user: demoUser }, error: null }
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      logger.info('User signed in successfully', { userId: data.user?.id })
      return { data, error: null }
    } catch (error: any) {
      logger.error('Sign in error', error)
      return { data: null, error }
    } finally {
      setState(prev => ({ ...prev, loading: false }))
    }
  }

  const signOut = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }))
      
      // Atualizar status offline antes de sair
      if (state.user && isSupabaseConfigured) {
        await updateOnlineStatus(state.user.id, false)
      }

      if (!isSupabaseConfigured) {
        // Modo demo
        setState(prev => ({
          ...prev,
          user: null,
          session: null,
          userType: null,
          loading: false
        }))
        return
      }

      const { error } = await supabase.auth.signOut()
      if (error) throw error

      logger.info('User signed out successfully')
    } catch (error: any) {
      logger.error('Sign out error', error)
    } finally {
      setState(prev => ({ ...prev, loading: false }))
    }
  }

  const resetPassword = async (email: string) => {
    try {
      if (!isSupabaseConfigured) {
        // Modo demo
        return { data: null, error: null }
      }

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) throw error

      logger.info('Password reset email sent', { email })
      return { data, error: null }
    } catch (error: any) {
      logger.error('Password reset error', error)
      return { data: null, error }
    }
  }

  const value: AuthContextType = {
    ...state,
    signUp,
    signIn,
    signOut,
    resetPassword
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)

  // Fallback defensivo: em alguns cenários (HMR/duplicação de bundle) o Context pode vir `undefined`.
  // Em telas públicas (ex: perfil) precisamos renderizar mesmo sem sessão.
  if (context === undefined) {
    logger.warn('useAuth used without AuthProvider; returning safe fallback')
    const notReady = async () => {
      throw new Error('AuthProvider missing')
    }
    return {
      user: null,
      session: null,
      loading: false,
      userType: null,
      signUp: notReady,
      signIn: notReady,
      signOut: async () => {},
      resetPassword: notReady
    } as AuthContextType
  }

  return context
}
